import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Sentry from "@sentry/browser";

import { Box, Grid } from "@mui/material";

import { IndexedCardInfo } from "@akj-dev/shared-components";

import { useDwsResellerEnabled } from "../../../shared/hooks/useDwsResellerEnabled";
import { useCanAccessVfDirect } from "../../../shared/hooks/useCanAccessVfDirect";
import { getHardwareCount } from "../../../store/hardware/selectors";
import { haveProductsRequiringDeliveryAddress } from "../../../store/universalProducts/selectors";
import {
  getMobileSIMsRequired,
  getMobileSIMsRequiredCount,
} from "../../../store/mobile/selectors/delivery";

import { HardwareDelivery } from "./Hardware";
import { EthernetContainer } from "./Ethernet/index";
import { WlrBroadbandContainer } from "./WlrBroadband";
import { ContactsContainer } from "./Contacts";
import { ShippingContainer } from "./Mobile/Shipping";
import { TrackingDetailsContainer } from "./Mobile/TrackingDetails";
import { OrderContainer } from "./Order";
import { BillingAddressSectionContainer } from "./BillingAddressSection";
import { NewOrderContainer } from "./NewOrder";
import { DeclinedCreditVetWarningContainer } from "../../Wizard/containers/DeclinedCreditVetWarningContainer";
import { LabelSIMContainer } from "./Mobile/LabelSIM";
import { AccountSettings } from "@akj-dev/affinity-platform";
import { getAccountSettings } from "../../../store/account/selectors";
import { fetchCreditCheckStatus } from "../../../store/account/actions";
import { CreditCheckStatus } from "./CreditCheckStatus";

const cardIndexes = ["A", "B", "C", "D", "E", "F", "G", "H"];

export const StepDeliveryContainer = () => {
  const dispatch = useDispatch();
  let cardIndex = -1;

  const orderId = useSelector((state: any) => state.order.id);
  const mobileSIMsRequired = useSelector<any, boolean>(getMobileSIMsRequired);

  const universalProductsRequireAddress = useSelector<any, boolean>(
    haveProductsRequiringDeliveryAddress
  );
  const mobileSIMsRequiredCount = useSelector<any, number>(
    getMobileSIMsRequiredCount
  );
  const hardwareProductCount = useSelector<any, number>(getHardwareCount);
  const ethernetProductCount = useSelector<any, number>(
    (state) => state.ethernetProducts.configurations.length
  );
  const wlrBroadbandProductCount = useSelector<any, number>(
    (state) => state.wlrBroadband.configurations.length
  );

  const accountSettings = useSelector<any, AccountSettings>(getAccountSettings);
  const dwsResellerEnabled = useDwsResellerEnabled();
  const canAccessVfDirect = useCanAccessVfDirect();
  const isNewAccountForCreditCheck = useSelector<any, boolean>(
    (state) =>
      state.account.createAccount.response?.data?.account
        ?.is_new_account_for_credit_check === 1
  );
  const canAccessCreditCheckStatus =
    !canAccessVfDirect && !isNewAccountForCreditCheck;

  useEffect(() => {
    Sentry.addBreadcrumb({
      message: `Step 3 (Delivery Details) mounted}`,
      category: "action",
    });
    canAccessCreditCheckStatus && dispatch(fetchCreditCheckStatus());
  }, [canAccessCreditCheckStatus, dispatch]);

  return (
    <Box>
      {canAccessVfDirect && <DeclinedCreditVetWarningContainer />}
      <IndexedCardInfo
        index={1}
        title="Please fill out the delivery information"
      >
        {canAccessCreditCheckStatus && <CreditCheckStatus />}
        {!orderId ? (
          <NewOrderContainer />
        ) : (
          <Grid container spacing={2}>
            {universalProductsRequireAddress ||
            hardwareProductCount ||
            mobileSIMsRequired ? (
              <>
                <Grid item sm={12}>
                  <HardwareDelivery cardIndex={cardIndexes[++cardIndex]} />
                </Grid>
                <Grid item sm={12}>
                  <ShippingContainer cardIndex={cardIndexes[++cardIndex]} />
                </Grid>
                <Grid item sm={12}>
                  <TrackingDetailsContainer
                    cardIndex={cardIndexes[++cardIndex]}
                  />
                </Grid>
                {mobileSIMsRequiredCount > 1 &&
                  accountSettings.as_can_access_label_sim === "1" && (
                    <Grid item sm={12}>
                      <LabelSIMContainer cardIndex={cardIndexes[++cardIndex]} />
                    </Grid>
                  )}
              </>
            ) : (
              <Grid item sm={12}>
                <BillingAddressSectionContainer
                  cardIndex={cardIndexes[++cardIndex]}
                />
              </Grid>
            )}
            {wlrBroadbandProductCount > 0 && (
              <Grid item sm={12}>
                <WlrBroadbandContainer cardIndex={cardIndexes[++cardIndex]} />
              </Grid>
            )}
            {ethernetProductCount > 0 && (
              <Grid item sm={12}>
                <EthernetContainer cardIndex={cardIndexes[++cardIndex]} />
              </Grid>
            )}
            {!dwsResellerEnabled && (
              <Grid item sm={12}>
                <ContactsContainer cardIndex={cardIndexes[++cardIndex]} />
              </Grid>
            )}
            {!dwsResellerEnabled && (
              <Grid item sm={12}>
                <OrderContainer cardIndex={cardIndexes[++cardIndex]} />
              </Grid>
            )}
          </Grid>
        )}
      </IndexedCardInfo>
    </Box>
  );
};
